import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, Injectable } from '@angular/core';
import {
	BUI_MESSAGE_RESOLVER, BUI_WYSIWYG_DYNAMIC_CONFIG, BUI_WYSIWYG_STATIC_CONFIG, BUI_WYSIWYG_TOOLBAR_RESOLVER, WysiwygToolbarResolver,
} from '@bas/ui';
import configuration from 'globalConfiguration';
import { Subject } from 'rxjs';
import { AppComponent } from './app.component';
import { BASErrorHandler } from './errorhandler/bas-error.handler';
import { BlobErrorInterceptor } from './errorhandler/blob-error.interceptor';
import { WebserviceExceptionInterceptor } from './errorhandler/webservice-exception/webservice-exception.interceptor';
import { WebserviceExceptionService } from './errorhandler/webservice-exception/webservice-exception.service';
import { getNodes } from './navigation/nodes/nodes';
import { Nodes } from './permissions/permissions.token';
import { BreadcrumbService } from './shared/generic-compounds/breadcrumb/breadcrumb.service';
import { LocaleProvider } from './shared/services/locale.provider';
import { BuiMessageResolver } from './shared/translate/bui-message-resolver';
import { StrutsComponent } from './struts/struts.component';
import { StrutsService } from './struts/struts.service';

@Injectable()
export class BuiWysiwygToolbarResolver implements WysiwygToolbarResolver {

	resolve(key: string): Array<Array<string>> {
		return configuration.CKEditorHandler.toolbars[`toolbar_${ key }`];
	}
}

export const sharedDeclarations = [
	AppComponent,
	StrutsComponent,
];

export const sharedProviders = [
	{ provide: Nodes, useValue: getNodes },
	{ provide: ErrorHandler, useClass: BASErrorHandler },
	{ provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
	{ provide: BUI_MESSAGE_RESOLVER, useClass: BuiMessageResolver },
	{
		provide: BUI_WYSIWYG_STATIC_CONFIG,
		useValue: () => ({
			Link: {
				HTMLAttributes: {
					class: 'defaultLink ajaxIgnore',
				},
			},
			Html5Video: {
				srcPrefix: 'fetch.dbentity?convert=mediapool&type=mediapool&content=video&key=',
			},
			FontSize: {
				fontSizes: configuration.CKEditorHandler.config.fontSize_sizes.split(';'),
			},
			Base64Image: {
				accept: 'image/jpeg,image/png,image/gif',
			},
			ImageUpload: {
				accept: 'image/jpeg,image/png,image/gif',
				onUpload: (images: Array<File>) => {
					const uploadPath = 'rest/cms/upload/picture';
					const praefix = 'fetch.dbentity?type=cms&convert=image%2Fjpg&content=image%2Fjpg&key=';
					const suffix = '_fullsize.img';
					const formData = new FormData();
					let templateKey;
					const cmsId = (document.getElementById('cmsId') as HTMLInputElement)?.value.split('.')[0];
					if (cmsId) {
						templateKey = cmsId + '_' + new Date().getTime();
					} else {
						templateKey = 'UI-Lab';
					}

					formData.append('templateKey', templateKey);
					formData.append('file', images[0]);

					const subject = new Subject();
					const xhr = new XMLHttpRequest();
					xhr.addEventListener('load', () => {
						subject.next([
							{
								src: praefix + encodeURIComponent(templateKey) + suffix,
							},
						]);
					});
					xhr.open('POST', uploadPath);
					xhr.send(formData);
					return subject;
				},
			},
			Styles: {
				element: 'font',
				stylesets: Object.fromEntries(configuration.CKEditorHandler.stylesets?.styles?.map(style => [
					style.name, style.attributes.class,
				]) ?? []),
			},
			Anchor: {
				transforms: {
					input: (html: string) => {
						const doc = new DOMParser().parseFromString(html, 'text/html');
						const anchors = doc.querySelectorAll('a');
						anchors.forEach((originalA) => {
							const id = originalA.id || originalA.getAttribute('name');

							// <a> ohne id (bzw. name) ist kein Anker (im Sinne von Sprungziel) -> nichts ersetzen
							if (!id) {
								return;
							}

							const span = document.createElement('span');
							span.setAttribute('data-type', 'jumpAnchor');
							span.setAttribute('id', id);

							const content = originalA.childNodes;
							const href = originalA.href;
							// <a> ohne href oder content ist kein klickbarer Link -> content (wenn vorhanden) hinter den Anker
							if (!content) {
								originalA.replaceWith(span);
								return;
							}
							if (!href) {
								originalA.replaceWith(span, ...content);
								return;
							}

							// <a> mit href und content ist ein klickbarer Link -> Anker vor den Link
							const a = document.createElement('a');
							a.append(...content);
							a.href = href;
							a.target = originalA.target;
							a.className = originalA.className;
							originalA.replaceWith(span, a);
						});
						return doc.body.innerHTML;
					},
				},
			},
		}),
	},
	{
		provide: BUI_WYSIWYG_DYNAMIC_CONFIG,
		useValue: {
			Placeholder: (config) => {
				const placeholderInput = document.querySelector('#templatePlaceholder') as HTMLInputElement;
				const inputValue = placeholderInput?.value;
				if (!inputValue) {
					return config;
				}
				const value = JSON.parse(inputValue) as Record<string, {
					description: string,
					example: string
				}>;
				const valueEntries = Object.entries(value);
				const placeholderEntries = valueEntries.map(([key, value]) => [key, { label: value.description, title: value.example }]);
				const placeholders = Object.fromEntries(placeholderEntries);
				return ({
					...config,
					delimiter: '$',
					placeholders,
					transforms: {
						input(html: string): string {
							return html.replace(/\$([\w-]+)\$/g, (match, key) => key in placeholders
								? `<span data-type="placeholder" data-id="${ key }">$${ key }$</span>`
								: match);
						},
						output(html: string): string {
							return html.replace(/<span data-type="placeholder" data-id="([\w-]+)">\$([\w-]+)\$<\/span>/g, (
								match,
								key,
							) => `$${ key }$`);
						},
					},
				});
			},
		},
	},
	{ provide: BUI_WYSIWYG_TOOLBAR_RESOLVER, useClass: BuiWysiwygToolbarResolver },
	{ provide: HTTP_INTERCEPTORS, useClass: WebserviceExceptionInterceptor, multi: true },
	{ provide: HTTP_INTERCEPTORS, useClass: BlobErrorInterceptor, multi: true },
	LocaleProvider,
	StrutsService,
	WebserviceExceptionService,
	BreadcrumbService,
];
